import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {IconBox} from '../../../../../../libs/shared/ui/src/components/iconBox/iconBox';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {buildConversationPublicId} from '../../../../../helpers/models/conversationHelpers';
import {
  DetailedActivityModel,
  LinkedConversationActivityRolesEnum,
} from '../../../../../models/api/activityModel';
import {CommentUnfurlTypesEnum} from '../../../../../models/api/commentUnfurlModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';
import {TeammateCardPreviewer, TeammateNamePreviewer} from '../../../../teammateCard/teammateCardPreviewer';
import {CommentHeaderIcon} from '../comment/commentHeaderIcon';
import {CommentViewerUnfurls} from '../comment/commentViewerUnfurls';

interface LinkedConversationViewerProps {
  panelId: ViewerPanelId;
  isFirstOfKind: boolean;
  activity: DetailedActivityModel;
  linkedConversation: ConversationModel;
  currentConversation: ConversationModel | undefined;
  sourceTeammate: TeammateModel | undefined;
}

const StyledTimestampWrapperDiv = styled.div`
  grid-area: timestamp;
  /** Full-width to allow displaying on hover for entire cell. */
  width: 100%;
  vertical-align: middle;

  text-align: right;
  justify-self: end;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade70};

  /* Visible on hover. */
  opacity: 0;
`;

const StyledTimestampDiv = styled.div`
  position: relative;
  margin-top: 12px;
  margin-right: 4px;
`;

const StyledLinkedConversationContainerDiv = styled.div`
  display: grid;
  grid-template-areas:
    '. title .'
    'icon content timestamp';
  grid-template-columns: 32px 1fr;

  &:hover ${StyledTimestampWrapperDiv} {
    opacity: 1;
  }
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  margin-left: -4px;
  gap: 5px;
`;

const StyledContentDiv = styled.div`
  grid-area: content;
  margin-left: -7px;
  margin-bottom: -12px;
`;

const StyledIconBox = styled(IconBox)`
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 30px;
  width: 30px;
  margin-top: 4px;
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  margin-top: 4px;
`;

const StyledUnlinkedContentDiv = styled(StyledContentDiv)`
  width: 260px;
  background-color: transparent;
  margin: 4px 6px 0;

  padding: 12px 0 12px 28px;

  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
`;

const StyledUnlinkedTextDiv = styled.div`
  font-style: italic;
  font-size: ${fontSizes.medium};
`;

const StyledUnlinkedBoldText = styled.strong`
  font-weight: ${fontWeights.semibold};
`;

export const LinkedConversationViewer: FC<LinkedConversationViewerProps> = (props) => {
  const {panelId, activity, currentConversation, linkedConversation, sourceTeammate} = props;

  const theme = useUserTheme();
  const {trackDataEvent} = useDataEventTracking();

  const renderTitle = () => {
    const isOriginalConversation =
      activity.linkedConversationRole === LinkedConversationActivityRolesEnum.LINKED;

    if (!sourceTeammate) {
      return isOriginalConversation ? (
        <Trans id="viewer-original-conversation-anon">Linked</Trans>
      ) : (
        <Trans id="viewer-linked-conversation-anon">Linked to original</Trans>
      );
    }

    if (isOriginalConversation) {
      return (
        <div>
          <Trans id="viewer-original-conversation">
            <TeammateNamePreviewer teammate={sourceTeammate} panelId={panelId} shouldCapitalizeYou /> linked
          </Trans>
        </div>
      );
    }

    return (
      <div>
        <Trans id="viewer-linked-conversation">
          <TeammateNamePreviewer teammate={sourceTeammate} panelId={panelId} shouldCapitalizeYou /> linked to
          original
        </Trans>
      </div>
    );
  };

  const renderUnlinkedActivity = () => (
    <StyledLinkedConversationContainerDiv>
      {props.isFirstOfKind && (
        <>
          <StyledIconBox
            iconName="unlinkedConversationsFilledSmall"
            backgroundColor={theme.greys.shade30}
            borderRadius={30}
            iconColor={theme.greys.shade70}
          />
          <StyledTitleDiv>{renderTitle()}</StyledTitleDiv>
        </>
      )}

      <StyledUnlinkedContentDiv>
        <StyledUnlinkedTextDiv>
          This conversation was
          <StyledUnlinkedBoldText> unlinked</StyledUnlinkedBoldText>
        </StyledUnlinkedTextDiv>
      </StyledUnlinkedContentDiv>
      <StyledTimestampWrapperDiv>
        <StyledTimestampDiv>
          <InteractiveDateTimeFormatter
            relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
            value={activity.date}
          />
        </StyledTimestampDiv>
      </StyledTimestampWrapperDiv>
    </StyledLinkedConversationContainerDiv>
  );

  const onLinkedActivityClick = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.VIEW_LINKED_CONVERSATION, {
      sourceType: currentConversation?.linkStatus,
      sourceConversationId: currentConversation?.id,
      targetConversationId: props.linkedConversation.id,
    });
  };

  const computeUnfurl = () => ({
    type: CommentUnfurlTypesEnum.LINKED,
    token: buildConversationPublicId(linkedConversation),
    conversationId: linkedConversation.id,
    annotationUid: activity.annotationUid,
    accessLink: activity.linkedConversationPermalink,
    activityId: activity.referencedActivityId,
  });

  const renderLinkedActivity = () => (
    <StyledLinkedConversationContainerDiv onClick={onLinkedActivityClick}>
      {props.isFirstOfKind && (
        <>
          {sourceTeammate && (
            <StyledIconDiv>
              <TeammateCardPreviewer panelId={panelId} teammate={sourceTeammate}>
                <TeammateAvatar
                  teammate={sourceTeammate}
                  size={VisualSizesEnum.LARGE}
                  supportsHover
                  hasDarkerHover
                />
              </TeammateCardPreviewer>
            </StyledIconDiv>
          )}
          {!sourceTeammate && (
            <StyledIconBox
              iconName="linkedConversationsFilledSmall"
              backgroundColor={theme.greys.shade30}
              borderRadius={30}
              iconColor={theme.greys.shade70}
            />
          )}
          <StyledTitleDiv>
            <CommentHeaderIcon iconName="linkedConversationsTiny" backgroundColor={theme.greys.shade80} />
            {renderTitle()}
          </StyledTitleDiv>
        </>
      )}
      <StyledContentDiv>
        <CommentViewerUnfurls
          panelId={panelId}
          conversationId={currentConversation?.id}
          unfurls={[computeUnfurl()]}
        />
      </StyledContentDiv>
      <StyledTimestampWrapperDiv>
        <StyledTimestampDiv>
          <InteractiveDateTimeFormatter
            relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
            value={activity.date}
          />
        </StyledTimestampDiv>
      </StyledTimestampWrapperDiv>
    </StyledLinkedConversationContainerDiv>
  );

  if (!currentConversation || currentConversation.linkStatus !== LinkedConversationStatusesEnum.UNLINKED) {
    return renderLinkedActivity();
  }

  return renderUnlinkedActivity();
};
